/**
 * tts-book.js — разбор книги на главы для /text-to-speech.
 *
 * ── Зачем это здесь, а не отдельным тулом ──────────────────────────────────
 * «Книга в машину» — это не отдельная задача, а тот же путь озвучки, у
 * которого сломан ВХОД. Сейчас книга приезжает в поле одним куском, обрезается
 * по суточному лимиту символов, и человек получает первые несколько тысяч
 * знаков — то есть начало предисловия. Отдельный инструмент этого не чинит:
 * синтез, голоса, ударения, счётчик и выдача уже здесь и работают.
 *
 * ── Что действительно отсутствовало ────────────────────────────────────────
 * 1. EPUB не принимался ВООБЩЕ — а это основной формат книг. Принимались txt,
 *    md, rtf, docx, doc, pdf, fb2, то есть всё, кроме книги.
 * 2. Не было понятия главы. Лимит символов никуда не денется — синтез стоит
 *    денег, — но при главах он перестаёт быть обрывом: человек берёт главу,
 *    слушает её в дороге, назавтра берёт следующую. Обрезка на середине фразы
 *    превращается в выбор.
 *
 * ── Почему свой распаковщик ZIP, а не JSZip ────────────────────────────────
 * EPUB — это ZIP, а распаковка одного дефлейт-потока в браузере с 2023 года
 * встроена (`DecompressionStream('deflate-raw')`). Тянуть ради этого 100 КБ
 * библиотеки на страницу, которая и так грузит голоса и словари ударений, —
 * плата без выгоды. Здесь читается центральный каталог и распаковываются
 * ровно те записи, которые нужны: контейнер, OPF, оглавление и файлы корешка.
 *
 * ⚠️ ZIP64 не поддерживается сознательно: он начинается за 4 ГБ, а книг такого
 * размера не бывает. Отказ при этом ЯВНЫЙ (`epub_zip64`), а не тихий мусор.
 */
(function () {
'use strict';

if (window.TtsBook) return;

/* ── ZIP ────────────────────────────────────────────────────────────────── */

var EOCD = 0x06054b50, CEN = 0x02014b50;

function findEocd(view, len) {
    // Комментарий архива до 64 КБ, поэтому подпись ищется с конца в окне.
    var max = Math.min(len, 66000);
    for (var i = len - 22; i >= len - max && i >= 0; i--) {
        if (view.getUint32(i, true) === EOCD) return i;
    }
    return -1;
}

async function readZip(file) {
    var buf = await file.arrayBuffer();
    var view = new DataView(buf);
    var eocd = findEocd(view, buf.byteLength);
    if (eocd < 0) throw new Error('epub_not_zip');

    var count = view.getUint16(eocd + 10, true);
    var cdOff = view.getUint32(eocd + 16, true);
    if (cdOff === 0xFFFFFFFF || count === 0xFFFF) throw new Error('epub_zip64');

    var entries = {}, p = cdOff;
    for (var i = 0; i < count; i++) {
        if (view.getUint32(p, true) !== CEN) break;
        var method = view.getUint16(p + 10, true);
        var csize = view.getUint32(p + 20, true);
        var nameLen = view.getUint16(p + 28, true);
        var extraLen = view.getUint16(p + 30, true);
        var cmtLen = view.getUint16(p + 32, true);
        var lho = view.getUint32(p + 42, true);
        var name = new TextDecoder('utf-8').decode(new Uint8Array(buf, p + 46, nameLen));
        entries[name] = { method: method, csize: csize, lho: lho };
        p += 46 + nameLen + extraLen + cmtLen;
    }
    return { buf: buf, view: view, entries: entries };
}

async function readEntry(zip, name) {
    var e = zip.entries[name];
    if (!e) return null;
    // Длины имени и «лишнего поля» в ЛОКАЛЬНОМ заголовке свои: брать их из
    // центрального каталога нельзя, они там регулярно другие.
    var nameLen = zip.view.getUint16(e.lho + 26, true);
    var extraLen = zip.view.getUint16(e.lho + 28, true);
    var start = e.lho + 30 + nameLen + extraLen;
    var raw = new Uint8Array(zip.buf, start, e.csize);
    if (e.method === 0) return new TextDecoder('utf-8').decode(raw);
    if (e.method !== 8) throw new Error('epub_method_' + e.method);
    if (typeof DecompressionStream === 'undefined') throw new Error('epub_no_inflate');
    var ds = new DecompressionStream('deflate-raw');
    var out = new Response(new Blob([raw]).stream().pipeThrough(ds));
    return new TextDecoder('utf-8').decode(new Uint8Array(await out.arrayBuffer()));
}

/* ── текст из разметки ──────────────────────────────────────────────────── */

var BLOCK = /^(P|DIV|BR|H1|H2|H3|H4|H5|H6|LI|TR|SECTION|ARTICLE|BLOCKQUOTE|PRE|TD)$/;

function textFromDom(root) {
    var out = [];
    (function walk(n) {
        for (var c = n.firstChild; c; c = c.nextSibling) {
            if (c.nodeType === 3) { out.push(c.nodeValue); continue; }
            if (c.nodeType !== 1) continue;
            var tag = c.tagName ? c.tagName.toUpperCase() : '';
            if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'RT' || tag === 'RP') continue;
            walk(c);
            if (BLOCK.test(tag)) out.push('\n');
        }
    })(root);
    return out.join('')
        .replace(/\r\n?/g, '\n')
        .replace(/[ \t ]+/g, ' ')
        .replace(/ *\n */g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

function parseXml(text, mime) {
    var doc = new DOMParser().parseFromString(text, mime);
    // parsererror приходит НЕ исключением: молча вернувшийся мусорный документ
    // выглядел бы как книга без глав.
    if (doc.getElementsByTagName('parsererror').length) return null;
    return doc;
}

/* ── EPUB ───────────────────────────────────────────────────────────────── */

function resolvePath(base, href) {
    href = String(href || '').split('#')[0];
    if (!href) return '';
    if (href.charAt(0) === '/') return href.slice(1);
    var parts = base ? base.split('/') : [];
    parts.pop();
    var segs = href.split('/');
    for (var i = 0; i < segs.length; i++) {
        if (segs[i] === '.' || segs[i] === '') continue;
        if (segs[i] === '..') parts.pop();
        else parts.push(segs[i]);
    }
    return parts.join('/');
}

/* Порог короткого документа. Для озвучки (по умолчанию 200 знаков) обложка и
   титул — не главы, их выбор только мешает. Читалке (/reading-ruler) нужна
   ВСЯ книга: у детской книжки глава бывает в три строки, и при пороге 200
   такая книга целиком кончалась отказом `epub_empty`. Поэтому порог — опция. */
function minCharsOf(opts) {
    return opts && typeof opts.minChars === 'number' ? opts.minChars : 200;
}

async function readEpub(file, opts) {
    var minChars = minCharsOf(opts);
    var zip = await readZip(file);

    var container = await readEntry(zip, 'META-INF/container.xml');
    if (!container) throw new Error('epub_no_container');
    var cdoc = parseXml(container, 'application/xml');
    var rootEl = cdoc && cdoc.querySelector('rootfile');
    var opfPath = rootEl && rootEl.getAttribute('full-path');
    if (!opfPath) throw new Error('epub_no_opf');

    var opfText = await readEntry(zip, opfPath);
    var opf = opfText && parseXml(opfText, 'application/xml');
    if (!opf) throw new Error('epub_no_opf');

    var bookTitle = '';
    var t = opf.getElementsByTagName('dc:title')[0] || opf.getElementsByTagName('title')[0];
    if (t) bookTitle = (t.textContent || '').trim();

    var manifest = {}, navPath = '', ncxPath = '';
    var items = opf.getElementsByTagName('item');
    for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var id = it.getAttribute('id'), href = it.getAttribute('href') || '';
        var type = it.getAttribute('media-type') || '';
        var props = it.getAttribute('properties') || '';
        manifest[id] = { path: resolvePath(opfPath, href), type: type };
        if (props.indexOf('nav') !== -1) navPath = manifest[id].path;
        if (type.indexOf('dtbncx') !== -1) ncxPath = manifest[id].path;
    }

    // Оглавление даёт НАЗВАНИЯ глав. Без него остаются заголовки внутри
    // документов, а без них — имена файлов; последнее человеку бесполезно, но
    // лучше пустой строки.
    var titles = {};
    try {
        if (navPath) {
            var navDoc = parseXml(await readEntry(zip, navPath), 'text/html');
            var links = navDoc ? navDoc.querySelectorAll('nav a, a') : [];
            for (var n = 0; n < links.length; n++) {
                var p = resolvePath(navPath, links[n].getAttribute('href'));
                if (p && !titles[p]) titles[p] = (links[n].textContent || '').trim();
            }
        } else if (ncxPath) {
            var ncx = parseXml(await readEntry(zip, ncxPath), 'application/xml');
            var points = ncx ? ncx.getElementsByTagName('navPoint') : [];
            for (var q = 0; q < points.length; q++) {
                var c = points[q].getElementsByTagName('content')[0];
                var lab = points[q].getElementsByTagName('text')[0];
                if (!c) continue;
                var pp = resolvePath(ncxPath, c.getAttribute('src'));
                if (pp && !titles[pp]) titles[pp] = lab ? (lab.textContent || '').trim() : '';
            }
        }
    } catch (e) { /* оглавление — украшение, его отсутствие не отказ */ }

    var spine = opf.getElementsByTagName('itemref');
    var chapters = [];
    for (var s = 0; s < spine.length; s++) {
        var ref = spine[s].getAttribute('idref');
        var m = manifest[ref];
        if (!m || m.type.indexOf('html') === -1) continue;
        var html;
        try { html = await readEntry(zip, m.path); } catch (e) { continue; }
        if (!html) continue;
        var doc = parseXml(html, 'text/html');
        if (!doc) continue;
        var text = textFromDom(doc.body || doc.documentElement);
        // Обложка, титул и страница выходных данных — это не главы: в них
        // десяток слов, и в списке они только мешают выбирать.
        if (!text || text.length < minChars) continue;
        var head = doc.querySelector('h1, h2, h3');
        var title = titles[m.path] || (head ? (head.textContent || '').trim() : '')
            || m.path.split('/').pop();
        chapters.push({ title: title.slice(0, 120), text: text });
    }
    if (!chapters.length) throw new Error('epub_empty');
    return { kind: 'epub', title: bookTitle, chapters: chapters };
}

/* ── FB2 ────────────────────────────────────────────────────────────────── */

function readFb2(xmlText, opts) {
    var minChars = minCharsOf(opts);
    var doc = parseXml(xmlText, 'application/xml');
    if (!doc) return null;
    var bodies = doc.getElementsByTagName('body');
    if (!bodies.length) return null;
    var bookTitle = '';
    var bt = doc.getElementsByTagName('book-title')[0];
    if (bt) bookTitle = (bt.textContent || '').trim();

    var chapters = [];
    // Только секции ПЕРВОГО уровня: вложенные — это подразделы главы, и в
    // списке они дробят книгу на куски по абзацу.
    for (var b = 0; b < bodies.length; b++) {
        if ((bodies[b].getAttribute('name') || '') === 'notes') continue;
        var kids = bodies[b].children || [];
        for (var i = 0; i < kids.length; i++) {
            if (kids[i].tagName !== 'section') continue;
            var titleEl = kids[i].getElementsByTagName('title')[0];
            var title = titleEl ? (titleEl.textContent || '').replace(/\s+/g, ' ').trim() : '';
            var text = textFromDom(kids[i]);
            if (!text || text.length < minChars) continue;
            chapters.push({ title: title.slice(0, 120), text: text });
        }
    }
    return chapters.length ? { kind: 'fb2', title: bookTitle, chapters: chapters } : null;
}

/* ── главы в плоском тексте ─────────────────────────────────────────────── */

/* Ключевые слова всех наших локалей. Список намеренно короткий: чем он шире,
   тем чаще обычная строка текста объявляется заголовком и книга рассыпается
   на сотню «глав» по абзацу. */
/* ⚠️ Граница слова `\b` здесь НЕ работает: в JS она определена через ASCII-
   класс `\w`, а «глава» кончается кириллической «а», которой в `\w` нет.
   Границы между «а» и пробелом для движка нет, и вся русская — как и украинская,
   арабская, корейская — половина списка молча не срабатывала: главы находились
   только в английских книгах. Ловит это лишь прогон на настоящем русском тексте.
   Вместо границы — «дальше не буква». */
var HEAD_WORD = new RegExp(
    '^\\s*(' +
    'глава|розділ|часть|частина|chapter|part|kapitel|teil|abschnitt|' +
    'cap[íi]tulo|parte|chapitre|partie|bab|bagian|الفصل|الجزء' +
    ')(?!\\p{L})', 'iu');
var HEAD_KO = /^\s*(제\s*\d+\s*(장|부)|\d+\s*장)\s*$/;
var HEAD_MD = /^\s{0,3}#{1,3}\s+\S/;
var HEAD_NUM = /^\s*(\d{1,3}|[IVXLC]{1,7})\s*[.．]?\s*$/;

function looksLikeHeading(line) {
    var s = line.trim();
    if (!s || s.length > 90) return false;
    if (HEAD_MD.test(line)) return true;
    if (HEAD_KO.test(s)) return true;
    if (HEAD_NUM.test(s)) return true;
    if (!HEAD_WORD.test(s)) return false;
    // «Глава дома была открыта» — предложение, а не заголовок: у заголовка нет
    // завершающей точки и он короткий.
    return !/[.!?…]$/.test(s);
}

function splitText(text) {
    var lines = String(text || '').split('\n');
    var marks = [];
    for (var i = 0; i < lines.length; i++) if (looksLikeHeading(lines[i])) marks.push(i);
    if (marks.length < 2) return null;

    var chapters = [];
    for (var m = 0; m < marks.length; m++) {
        var from = marks[m], to = (m + 1 < marks.length) ? marks[m + 1] : lines.length;
        var title = lines[from].replace(/^\s*#{1,3}\s*/, '').trim();
        var body = lines.slice(from + 1, to).join('\n').trim();
        if (body.length < 200) continue;
        chapters.push({ title: title.slice(0, 120), text: (title ? title + '\n\n' : '') + body });
    }
    return chapters.length >= 2 ? { kind: 'headings', title: '', chapters: chapters } : null;
}

/* ── передняя дверь ─────────────────────────────────────────────────────── */

/**
 * @param {File} file
 * @param {string} ext        расширение в нижнем регистре
 * @param {string} flatText   уже извлечённый плоский текст (для docx/pdf/txt)
 * @returns {Promise<null|{kind,title,chapters:[{title,text}]}>}
 */
async function parse(file, ext, flatText) {
    if (ext === 'epub') return readEpub(file);
    if (ext === 'fb2') {
        var byXml = readFb2(flatText && flatText.indexOf('<') === 0 ? flatText : await file.text());
        if (byXml) return byXml;
    }
    return splitText(flatText || '');
}

/** Отпечаток книги — ключ памяти о том, какие главы уже забрали. */
function fingerprint(file) {
    return 'ttsbook:' + [file.name || '?', file.size || 0, file.lastModified || 0].join('|');
}

window.TtsBook = {
    parse: parse,
    splitText: splitText,
    readFb2: readFb2,
    readEpub: readEpub,
    textFromDom: textFromDom,
    fingerprint: fingerprint,
};
})();
